import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AvatarChip } from "./AvatarChip";
import { TierBadge } from "./TierBadge";
import { ReportImageModal } from "./ReportImageModal";
import { useAuth } from "../context/AuthContext";
import CreateTeamModal from "./CreateTeamModal";
import { fetchHqs, fetchRecruitment, fetchGames, fetchMyLeagues, joinOpenTeam, fetchRoleSuggestions } from "../lib/api";

/**
 * HqDiscoveryPage — `/hqs` route.
 *
 * Two stacked sections:
 *  1. RECRUITMENT POSTS — fastest path to a tryout/offer.
 *  2. HQ DIRECTORY — grid of public, non-private HQs filtered by game /
 *     region / competitive level / recruitment status.
 *
 * Cards are click-throughs that open the HQ's parent league overview
 * (existing route) because that's where the team's request-to-join button
 * lives. Direct HQ profile pages can be added once the slug field is set.
 */
export function HqDiscoveryPage({ onOpenLeague, onToast }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    game: "", region: "", competitive_level: "", recruitment_status: "",
    seeking_role: "", q: "",
  });
  const [roleSuggestions, setRoleSuggestions] = useState([]);

  // Sync the dropdown with the backend taxonomy so it never falls out of date.
  useEffect(() => {
    fetchRoleSuggestions(filters.game || undefined)
      .then((r) => setRoleSuggestions(Array.isArray(r) ? r : []))
      .catch(() => setRoleSuggestions([]));
  }, [filters.game]);
  const [hqs, setHqs] = useState([]);
  const [posts, setPosts] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreatePicker, setShowCreatePicker] = useState(false);
  const [myLeagues, setMyLeagues] = useState([]);
  const [reportImage, setReportImage] = useState(null);  // { user_id, team_id, team_name, _kind }
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v && String(v).trim())
      );
      const [list, recs] = await Promise.all([
        fetchHqs(params),
        fetchRecruitment(params.game ? { game: params.game } : {}),
      ]);
      setHqs(list);
      setPosts(recs);
    } catch (e) {
      onToast?.("Couldn't load teams.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); fetchGames().then(setGames).catch(() => {}); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [filters]);

  const onCreate = async () => {
    if (!user) { onToast?.("Sign in first to launch a team."); return; }
    // Standalone teams are the first-class create flow now. The user
    // can build per-game/per-split rosters from the Team Hub once it's
    // live, without ever having to join a league first.
    setCreateOpen(true);
  };

  return (
    <main className="page-z max-w-6xl mx-auto px-4 pt-10 pb-20" data-testid="hq-discovery-page">
      {/* ============================ HERO ============================ */}
      <section
        data-testid="hq-hero"
        className="relative overflow-hidden rounded-3xl border border-[#14b8a6]/40 mb-8"
      >
        {/* gradient + grain */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#14b8a6]/30 via-[#14b8a6]/15 to-[#14b8a6]/20" aria-hidden />
        <div
          className="absolute inset-0 opacity-30 mix-blend-screen pointer-events-none"
          aria-hidden
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, rgba(20, 184, 166,0.45), transparent 55%), radial-gradient(circle at 80% 80%, rgba(20, 184, 166,0.35), transparent 55%)",
          }}
        />
        <div className="relative p-5 sm:p-10 lg:p-12 flex flex-col lg:flex-row items-start lg:items-center gap-6">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] tracking-[4px] text-[#14b8a6] font-bold">
              <i className="fas fa-flag mr-1.5" />TEAMS DASHBOARD
            </p>
            <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl grad-text mt-2 leading-tight">
              CREATE TEAM
            </h1>
            <p className="text-sm sm:text-base text-white/75 mt-3 max-w-xl leading-relaxed">
              Build your esports organization, recruit free agents, and dominate
              the leaderboards. Custom rosters · per-game captains · public TEAM
              profile in minutes.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={onCreate}
                data-testid="hq-hero-create-btn"
                className="group relative inline-flex items-center gap-2 px-7 py-3.5 rounded-2xl bg-gradient-to-r from-[#14b8a6] via-[#14b8a6] to-[#14b8a6] text-black font-bold tracking-widest text-sm shadow-[0_0_30px_rgba(20, 184, 166,0.55)] hover:scale-[1.03] active:scale-[0.99] transition-transform"
              >
                <i className="fas fa-plus" />
                CREATE TEAM
                <i className="fas fa-arrow-right ml-1 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="#hq-directory"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl border border-white/15 text-white/80 hover:text-white hover:bg-white/5 text-[12px] tracking-widest"
              >
                <i className="fas fa-binoculars" />
                BROWSE TEAMS
              </a>
            </div>
          </div>
          {/* trio of stat chips */}
          <div className="grid grid-cols-3 gap-3 w-full lg:w-auto">
            <HeroStat icon="fa-users" label="TEAMS" value={hqs.length} accent="text-[#14b8a6]" />
            <HeroStat icon="fa-bullhorn" label="OPEN ROLES" value={posts.length} accent="text-[#14b8a6]" />
            <HeroStat icon="fa-flag-checkered" label="GAMES" value={games.length} accent="text-[#14b8a6]" />
          </div>
        </div>
      </section>

      {/* Filter strip */}
      <section className="glass rounded-2xl p-4 mb-6 grid sm:grid-cols-5 gap-3" data-testid="hq-filters">
        <input
          value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          placeholder="Search teams…"
          data-testid="hq-filter-q"
          className="bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm placeholder-white/30"
        />
        <select
          value={filters.game}
          onChange={(e) => setFilters((f) => ({ ...f, game: e.target.value }))}
          data-testid="hq-filter-game"
          className="bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white text-sm"
        >
          <option value="">All games</option>
          {games.map((g) => <option key={g.slug} value={g.name}>{g.name}</option>)}
        </select>
        <select
          value={filters.competitive_level}
          onChange={(e) => setFilters((f) => ({ ...f, competitive_level: e.target.value }))}
          data-testid="hq-filter-level"
          className="bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white text-sm"
        >
          <option value="">Any level</option>
          <option value="casual">Casual</option>
          <option value="competitive">Competitive</option>
          <option value="pro">Pro</option>
        </select>
        <select
          value={filters.recruitment_status}
          onChange={(e) => setFilters((f) => ({ ...f, recruitment_status: e.target.value }))}
          data-testid="hq-filter-recruit"
          className="bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white text-sm"
        >
          <option value="">Any status</option>
          <option value="open">Open recruiting</option>
          <option value="scouting">Scouting</option>
          <option value="closed">Closed</option>
        </select>
        <input
          value={filters.region}
          onChange={(e) => setFilters((f) => ({ ...f, region: e.target.value }))}
          placeholder="Region (NA, EU, …)"
          data-testid="hq-filter-region"
          className="bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm placeholder-white/30"
        />
        <select
          value={filters.seeking_role}
          onChange={(e) => setFilters((f) => ({ ...f, seeking_role: e.target.value }))}
          data-testid="hq-filter-role"
          className="bg-black/40 border border-[#14b8a6]/40 rounded-xl px-3 py-2 text-white text-sm"
          title="Show teams seeking a specific role"
        >
          <option value="">Any role</option>
          {roleSuggestions.map((r) => (
            <option key={r.slug} value={r.slug}>{r.label}</option>
          ))}
        </select>
      </section>

      {/* Recruitment posts */}
      {posts.length > 0 && (
        <section className="mb-8" data-testid="hq-recruitment">
          <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold mb-2">
            <i className="fas fa-bullhorn mr-1" />OPEN RECRUITMENT · {posts.length}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {posts.slice(0, 9).map((p) => (
              <button
                key={p.post_id}
                type="button"
                onClick={() => onOpenLeague?.(p.league_id)}
                data-testid={`recruit-card-${p.post_id}`}
                className="text-left glass rounded-2xl p-3 border border-[#14b8a6]/30 hover:border-[#14b8a6]/60 transition-colors"
              >
                <div className="flex items-center gap-2 mb-2">
                  <AvatarChip avatarId={p.team_avatar_id} pfpUrl={p.team_pfp_url} size={32} />
                  <div className="min-w-0">
                    <p className="text-[10px] tracking-widest text-[#14b8a6] font-bold truncate">
                      {p.role || "PLAYER WANTED"}
                    </p>
                    <p className="font-bold text-sm truncate">{p.team_name}</p>
                  </div>
                </div>
                <p className="text-[11px] text-white/65 line-clamp-2">{p.description || "Tap to view tryout details."}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  <span className="text-[10px] px-2 py-0.5 rounded-md bg-[#14b8a6]/15 text-[#14b8a6]">{p.game}</span>
                  {p.rank && <span className="text-[10px] px-2 py-0.5 rounded-md bg-white/8 text-white/65">{p.rank}</span>}
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* HQ grid */}
      <p id="hq-directory" className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold mb-2">
        <i className="fas fa-shield-halved mr-1" />TEAMS · {loading ? "..." : hqs.length}
      </p>
      {(!loading && hqs.length === 0) ? (
        <p className="text-sm text-white/45 py-10 text-center" data-testid="hq-empty">
          No teams match those filters. Loosen them or check back later.
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {hqs.map((hq) => <HqCard
            key={hq.team_id}
            hq={hq}
            onOpenLeague={onOpenLeague}
            onToast={onToast}
            onAfterJoin={load}
            user={user}
            onReportImage={(target) => user ? setReportImage(target) : onToast?.("Sign in to report.")}
          />)}
        </div>
      )}

      {showCreatePicker && (
        <CreateHqPickerModal
          myLeagues={myLeagues}
          onClose={() => setShowCreatePicker(false)}
          onPick={(leagueId) => {
            setShowCreatePicker(false);
            onOpenLeague?.(leagueId);  // opens LeagueDetailModal where + CREATE TEAM lives for owners
            onToast?.("Open the TEAMS tab to spin up your team.");
          }}
        />
      )}

      <ReportImageModal
        open={!!reportImage}
        target={reportImage}
        imageKind={reportImage?._kind}
        onClose={() => setReportImage(null)}
        onToast={onToast}
      />

      <CreateTeamModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onToast={onToast}
        onCreated={(team) => {
          setCreateOpen(false);
          // Refresh discovery so the new team appears, then deep-link
          // to the dashboard where the owner can open their HQ.
          load();
          navigate("/dashboard");
        }}
      />
    </main>
  );
}

function HeroStat({ icon, label, value, accent }) {
  return (
    <div className="rounded-2xl bg-black/45 backdrop-blur border border-white/10 px-4 py-3 text-center min-w-[88px]">
      <i className={`fas ${icon} ${accent}`} />
      <p className="font-display text-2xl text-white mt-1">{value}</p>
      <p className="text-[9px] tracking-[3px] text-white/45">{label}</p>
    </div>
  );
}

function CreateHqPickerModal({ myLeagues, onClose, onPick }) {
  // HQs live inside a league today — surface which leagues the user owns or
  // is in so they can pick where their new HQ goes. League owners can spin
  // up new HQs; members will see the "request to join an existing team"
  // hint inside the league overview.
  return (
    <div
      className="fixed inset-0 z-[95] bg-black/85 backdrop-blur-md p-4 flex items-center justify-center"
      onClick={onClose}
      data-testid="hq-create-picker-backdrop"
    >
      <div
        className="glass rounded-3xl p-6 w-full max-w-md max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="hq-create-picker-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
              <i className="fas fa-flag mr-1" />NEW TEAM
            </p>
            <h2 className="font-display text-2xl grad-text">Pick a league</h2>
            <p className="text-[11px] text-white/55 mt-1">
              Teams live inside a league. Choose which one houses your new team.
            </p>
          </div>
          <button onClick={onClose} data-testid="hq-create-picker-close"
                  className="text-white/60 hover:text-white text-2xl">
            <i className="fas fa-times" />
          </button>
        </div>
        <div className="space-y-2">
          {myLeagues.map((l) => (
            <button
              key={l.league_id}
              type="button"
              onClick={() => onPick(l.league_id)}
              data-testid={`hq-create-pick-${l.league_id}`}
              className="w-full text-left flex items-center gap-3 p-3 rounded-2xl bg-black/30 border border-white/10 hover:border-[#14b8a6]/50 transition-colors"
            >
              <div className="w-9 h-9 rounded-xl bg-[#14b8a6]/10 border border-[#14b8a6]/40 flex items-center justify-center text-[#14b8a6]">
                <i className={`fas ${l.is_owner ? "fa-crown" : "fa-trophy"}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm truncate text-white">{l.name}</p>
                <p className="text-[10px] text-[#a3a3ac] truncate">
                  {l.game} · {l.is_owner ? "you own this league" : "you're a member"}
                </p>
              </div>
              <i className="fas fa-arrow-right text-[#14b8a6]" />
            </button>
          ))}
        </div>
        <Link
          to="/leagues"
          onClick={onClose}
          className="block mt-4 text-center text-[10px] tracking-widest text-[#14b8a6] hover:text-white"
        >
          NONE OF THESE? CREATE A NEW LEAGUE →
        </Link>
      </div>
    </div>
  );
}

function HqCard({ hq, onOpenLeague, onToast, onAfterJoin, user, onReportImage }) {
  const [joining, setJoining] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [reqGame, setReqGame] = useState(""); // For standalone teams — picked by the visitor.
  const [reqOpen, setReqOpen] = useState(false);
  const cap = Number.isFinite(hq.max_members) ? hq.max_members : 8;
  const isFull = (hq.member_count || 0) >= cap;
  const isMember = user && (hq.captain_user_id === user.user_id);
  const isStandalone = !hq.league_id;
  const canJoinOpen = hq.is_open && !isFull && user && !isMember;
  // For standalone teams the visitor can hit "REQUEST" directly from the card.
  const canRequest =
    !!user && !isMember && !isFull && hq.recruitment_status !== "closed";

  const onJoin = async (e) => {
    e.stopPropagation();
    if (!user) { onToast?.("Sign in first to join a team."); return; }
    setJoining(true);
    try {
      await joinOpenTeam(hq.team_id);
      onToast?.(`Welcome to ${hq.name}.`);
      onAfterJoin?.();
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Couldn't join — try later.");
    } finally {
      setJoining(false);
    }
  };

  const onRequest = async (e) => {
    e.stopPropagation();
    if (!user) { onToast?.("Sign in first to request a join."); return; }
    // Standalone teams with declared games must get a game selection.
    if (isStandalone && (hq.supported_games || []).length > 0 && !reqGame) {
      setReqOpen(true);
      return;
    }
    setRequesting(true);
    try {
      const { requestJoinTeam } = await import("../lib/api");
      const body = reqGame ? { game: reqGame } : {};
      await requestJoinTeam(hq.team_id, body);
      onToast?.(`Request sent to ${hq.name}'s owner.`);
      setReqOpen(false);
      setReqGame("");
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Couldn't send request.");
    } finally { setRequesting(false); }
  };

  const recruitColor =
    hq.recruitment_status === "open" ? "text-[#14b8a6] border-[#14b8a6]/40 bg-[#14b8a6]/10" :
    hq.recruitment_status === "scouting" ? "text-[#14b8a6] border-[#14b8a6]/40 bg-[#14b8a6]/10" :
    "text-white/40 border-white/15 bg-black/40";
  const game = (hq.supported_games && hq.supported_games[0]) || hq.game || "";
  return (
    <div
      data-testid={`hq-card-${hq.team_id}`}
      className="group glass rounded-3xl overflow-hidden border border-white/10 hover:border-[#14b8a6]/60 transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(20, 184, 166,0.18)]"
    >
      <div
        className="h-24 bg-gradient-to-br from-[#14b8a6]/40 to-[#14b8a6]/20 relative"
        style={hq.banner_url ? { backgroundImage: `url(${hq.banner_url})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}
      >
        {hq.recruitment_status && (
          <span
            className={`absolute top-2 right-2 text-[9px] tracking-widest font-bold px-2 py-0.5 rounded-md border ${recruitColor}`}
          >
            {hq.recruitment_status === "open" ? "RECRUITING" :
             hq.recruitment_status === "scouting" ? "SCOUTING" : "CLOSED"}
          </span>
        )}
        {!hq.league_id && (
          <span
            data-testid={`hq-card-standalone-${hq.team_id}`}
            className="absolute bottom-2 right-2 text-[8px] tracking-[3px] font-bold px-2 py-0.5 rounded-md border border-[#14b8a6]/60 bg-black/60 text-[#14b8a6]"
            title="Standalone team — no parent league"
          >
            STANDALONE
          </span>
        )}
        {/* Inline report flag on the team banner — only when banner is a
            real custom upload, not the default cyan/violet gradient. */}
        {hq.banner_url && onReportImage && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onReportImage({
                user_id: hq.captain_user_id,
                team_id: hq.team_id,
                team_name: hq.name,
                _kind: "team_banner",
              });
            }}
            data-testid={`hq-card-report-banner-${hq.team_id}`}
            title="Report this team banner"
            aria-label="Report this team banner"
            className="absolute top-2 left-2 w-7 h-7 rounded-full bg-black/70 border border-[#14b8a6]/50 text-[#14b8a6] hover:bg-[#14b8a6] hover:text-black transition-colors flex items-center justify-center text-[11px] shadow-lg"
          >
            <i className="fas fa-flag" aria-hidden="true" />
          </button>
        )}
      </div>
      <div className="p-4 -mt-7">
        <div className="flex items-end gap-2">
          <div className="relative ring-4 ring-black/80 rounded-2xl">
            <AvatarChip avatarId={hq.avatar_id} pfpUrl={hq.pfp_url} size={56} />
            {/* Inline report flag on team crest (custom uploads only) */}
            {hq.pfp_url && onReportImage && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onReportImage({
                    user_id: hq.captain_user_id,
                    team_id: hq.team_id,
                    team_name: hq.name,
                    _kind: "team_pfp",
                  });
                }}
                data-testid={`hq-card-report-crest-${hq.team_id}`}
                title="Report this team crest"
                aria-label="Report this team crest"
                className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-black/85 border border-[#14b8a6]/50 text-[#14b8a6] hover:bg-[#14b8a6] hover:text-black transition-colors flex items-center justify-center text-[9px] shadow-lg"
              >
                <i className="fas fa-flag" aria-hidden="true" />
              </button>
            )}
          </div>
          <div className="min-w-0 flex-1 pb-1">
            <p className="font-display text-lg text-white truncate">{hq.name}</p>
            <p className="text-[10px] text-[#a3a3ac] truncate">
              <i className="fas fa-crown text-[#14b8a6] text-[9px] mr-1" />
              {hq.owner_name || "OWNER"} · {hq.region || "—"}
            </p>
          </div>
        </div>
        {hq.description && (
          <p className="text-[11px] text-white/65 mt-2 line-clamp-2">{hq.description}</p>
        )}
        <div className="flex flex-wrap gap-1 mt-2">
          {hq.tier && <TierBadge tier={hq.tier} mmr={hq.mmr} compact />}
          {game && (
            <span className="text-[10px] px-2 py-0.5 rounded-md bg-[#14b8a6]/10 text-[#14b8a6] border border-[#14b8a6]/30">
              {game}
            </span>
          )}
          {hq.competitive_level && (
            <span className="text-[10px] px-2 py-0.5 rounded-md bg-black/40 text-[#a3a3ac] border border-[#14b8a6]/20">
              {hq.competitive_level.toUpperCase()}
            </span>
          )}
          {hq.is_open && (
            <span
              data-testid={`hq-card-open-${hq.team_id}`}
              className="text-[10px] px-2 py-0.5 rounded-md bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 font-bold"
              title="Open team — any league member can join"
            >
              🟢 OPEN
            </span>
          )}
          {hq.is_beginner && (
            <span
              data-testid={`hq-card-beginner-${hq.team_id}`}
              className="text-[10px] px-2 py-0.5 rounded-md bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 font-bold"
              title="Beginner-friendly team"
            >
              🌱 BEGINNER
            </span>
          )}
        </div>
        {Array.isArray(hq.openings) && hq.openings.length > 0 && (
          <div
            data-testid={`hq-card-openings-${hq.team_id}`}
            className="mt-2 flex flex-wrap gap-1"
          >
            <span className="text-[9px] tracking-[2px] text-[#14b8a6]/80 font-bold pt-0.5 mr-0.5">
              SEEKING:
            </span>
            {hq.openings.slice(0, 6).map((r) => (
              <span
                key={r}
                data-testid={`hq-card-opening-${hq.team_id}-${r}`}
                className="text-[10px] tracking-widest px-1.5 py-0.5 rounded-md bg-[#14b8a6]/10 text-[#14b8a6] border border-[#14b8a6]/30 font-bold"
              >
                {r}
              </span>
            ))}
            {hq.openings.length > 6 && (
              <span className="text-[10px] text-white/45">+{hq.openings.length - 6}</span>
            )}
          </div>
        )}
        <div className="flex justify-between text-[10px] text-white/50 mt-3 pt-3 border-t border-white/10">
          <span><i className="fas fa-users mr-1" />{hq.member_count}/{cap} member{cap === 1 ? "" : "s"}</span>
          <span data-testid={`hq-card-record-${hq.team_id}`}>
            <i className="fas fa-trophy mr-1" />{hq.wins || 0}W · {hq.losses || 0}L
          </span>
          <span><i className="fas fa-list-ol mr-1" />{hq.roster_count} roster{hq.roster_count === 1 ? "" : "s"}</span>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={() => !isStandalone && onOpenLeague?.(hq.league_id)}
            disabled={isStandalone}
            data-testid={`hq-card-view-${hq.team_id}`}
            title={isStandalone ? "Standalone team — no parent league" : "Open league"}
            className="flex-1 text-[10px] tracking-widest px-3 py-2 rounded-xl bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25 group-hover:bg-[#14b8a6]/25 transition-colors disabled:opacity-45 disabled:cursor-not-allowed"
          >
            <i className={`fas ${isStandalone ? "fa-flag" : "fa-arrow-right-to-bracket"} mr-1`} />
            {isStandalone ? "STANDALONE" : "VIEW"}
          </button>
          {canRequest && !canJoinOpen && (
            <button
              type="button"
              onClick={onRequest}
              disabled={requesting}
              data-testid={`hq-card-request-${hq.team_id}`}
              className="flex-1 text-[10px] tracking-widest px-3 py-2 rounded-xl bg-[#14b8a6]/20 text-[#14b8a6] border border-[#14b8a6]/50 hover:bg-[#14b8a6]/30 transition-colors disabled:opacity-50"
            >
              <i className="fas fa-paper-plane mr-1" />
              {requesting ? "SENDING…" : "REQUEST"}
            </button>
          )}
          {canJoinOpen && (
            <button
              type="button"
              onClick={onJoin}
              disabled={joining}
              data-testid={`hq-card-join-open-${hq.team_id}`}
              className="flex-1 text-[10px] tracking-widest px-3 py-2 rounded-xl bg-[#14b8a6]/20 text-[#14b8a6] border border-[#14b8a6]/50 hover:bg-[#14b8a6]/30 transition-colors disabled:opacity-50"
            >
              <i className="fas fa-door-open mr-1" />{joining ? "JOINING…" : "JOIN"}
            </button>
          )}
          {hq.is_open && isFull && (
            <span
              data-testid={`hq-card-full-${hq.team_id}`}
              className="flex-1 text-[10px] tracking-widest px-3 py-2 rounded-xl bg-black/40 text-white/45 border border-white/10 text-center"
            >
              FULL · {cap}/{cap}
            </span>
          )}
        </div>
        {reqOpen && (
          <div
            data-testid={`hq-card-request-drawer-${hq.team_id}`}
            className="mt-3 rounded-xl border border-[#14b8a6]/40 bg-black/40 p-3 animate-fade-in"
          >
            <p className="text-[9px] tracking-[3px] text-[#14b8a6] font-bold mb-2">
              WHICH GAME ARE YOU APPLYING FOR?
            </p>
            <div className="flex flex-wrap gap-1 mb-2">
              {(hq.supported_games || []).map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setReqGame(g); }}
                  data-testid={`hq-card-request-game-${hq.team_id}-${g}`}
                  className={`text-[10px] tracking-widest px-2 py-1 rounded-md border transition-colors ${
                    reqGame === g
                      ? "bg-[#14b8a6]/25 border-[#14b8a6]/70 text-[#14b8a6]"
                      : "bg-black/40 border-white/15 text-white/70 hover:border-[#14b8a6]/40"
                  }`}
                >
                  {g.toUpperCase()}
                </button>
              ))}
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setReqOpen(false); setReqGame(""); }}
                data-testid={`hq-card-request-cancel-${hq.team_id}`}
                className="text-[10px] tracking-widest text-white/55 px-3 py-1.5 rounded-md hover:text-white"
              >
                CANCEL
              </button>
              <button
                type="button"
                onClick={onRequest}
                disabled={!reqGame || requesting}
                data-testid={`hq-card-request-send-${hq.team_id}`}
                className="text-[10px] tracking-widest font-bold px-3 py-1.5 rounded-md bg-[#14b8a6] text-black disabled:opacity-40 hover:bg-[#7ab1ff] transition-colors"
              >
                {requesting ? "SENDING…" : "SEND REQUEST"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default HqDiscoveryPage;
